import { createContext, useContext, type ReactNode } from 'react';
import { useStore } from 'zustand';
import type {
  BuscaDto,
  CanaisDto,
  ConvidarEntrada,
  ConviteParaAprovarDto,
  ImportacaoDto,
  MembrosDoQuadroDto,
  MudarPapelEntrada,
  OpcoesConviteDto,
  ResultadoImportacaoDto,
  EmpresaEuDto,
  EuDto,
  ResultadoConviteDto,
  VinculoTelegramDto,
} from '@nexital/shared';
import type { Espaco } from '../domain/espaco';
import type { EstadoColaboracao, StoreColaboracao } from './colaboracao-store';
import type { EstadoEnvio, StoreEnvio } from './envio-store';
import type { ApiConfiguracoes } from './portas-configuracoes';
import type { StoreNotas } from './notas-store';
import type { StoreNotificacoes } from './notificacoes-store';
import type { ApiNotas } from './portas-notas';
import type { ApiLicitacao, ArmazenamentoLocal, CanalTempoReal, Sessao } from './portas';
import type { EstadoQuadro, StoreQuadro } from './quadro-store';

/** Espaços de trabalho da pessoa conectada, para o seletor e o menu da barra superior (RQ-ACE-011). */
export type Espacos = {
  usuario: EuDto['usuario'];
  empresas: EmpresaEuDto[];
  atual: Espaco;
  /** Troca de quadro ou abre/fecha a licitação sem recarregar a página (a URL muda). */
  navegar(espaco: Espaco): void;
  sair(): void;
  /** Troca a foto de perfil (envio direto ao S3) e relê a conta. */
  enviarFoto(arquivo: File): Promise<void>;
  /** Canais externos da pessoa (RQ-MSG-007): ela vincula o próprio Telegram pelo link do bot. */
  canais: { obter(): Promise<CanaisDto>; vincularTelegram(): Promise<VinculoTelegramDto>; desvincularTelegram(): Promise<void> };
  /** Busca geral no quadro atual; com `processoUuid`, só no que é daquele processo. */
  buscar(termo: string, processoUuid?: string): Promise<BuscaDto>;
  /** Envio de arquivos (ADR-0019, ADR-0020): envia e acompanha a leitura no quadro atual; com processo, direto nele. */
  importar(arquivos: File[], processoUuid?: string): Promise<ResultadoImportacaoDto>;
  importacoes(): Promise<ImportacaoDto[]>;
  /** Convida para o quadro atual (RQ-ACE-003, ADR-0021): o perfil de quem convida decide o que pode. */
  convidar(dados: ConvidarEntrada): Promise<ResultadoConviteDto>;
  opcoesConvite(): Promise<OpcoesConviteDto>;
  conviteParaAprovar(uuid: string): Promise<ConviteParaAprovarDto>;
  decidirConvite(uuid: string, decisao: 'aprovar' | 'recusar', motivo?: string): Promise<void>;
  /** Membros do quadro atual (RQ-ACE-018): gestor direto, papel, remoção e convites pendentes. */
  membros: {
    listar(): Promise<MembrosDoQuadroDto>;
    trocarGestor(usuarioUuid: string, gestorUuid: string): Promise<void>;
    mudarPapel(usuarioUuid: string, entrada: MudarPapelEntrada): Promise<void>;
    remover(usuarioUuid: string, motivo: string): Promise<void>;
    reenviarConvite(uuid: string): Promise<void>;
    convidarDeNovo(uuid: string): Promise<ResultadoConviteDto>;
    cancelarConvite(uuid: string, motivo: string): Promise<void>;
  };
};

/** O que a apresentação recebe da composição (main). Componentes nunca importam a infraestrutura. */
export type Dependencias = {
  sessao: Sessao;
  storeQuadro: StoreQuadro;
  /** API da licitação aberta (o store dela é criado ao abrir). */
  apiLicitacao: ApiLicitacao;
  /** Tempo real do quadro (uma conexão por aba) e o estado de colaboração do quadro atual. */
  canal: CanalTempoReal;
  storeColaboracao: StoreColaboracao;
  /** Notas, post-its e setas do quadro atual (RQ-NOT-001 a RQ-NOT-004). */
  storeNotas: StoreNotas;
  /** API das notas (a licitação aberta cria o store de notas dela). */
  apiNotas: ApiNotas;
  /** Avisos da pessoa conectada (sino), um por sessão. */
  storeNotificacoes: StoreNotificacoes;
  /** Envio de arquivos e bandeja "Arquivos sem processo" do quadro atual (ADR-0020). */
  storeEnvio: StoreEnvio;
  /** Configurações da empresa atual (ADR-0022). */
  configuracoes: ApiConfiguracoes;
  armazenamento: ArmazenamentoLocal;
  espacos: Espacos;
};

const Contexto = createContext<Dependencias | null>(null);

export function ProvedorDependencias({ valor, children }: { valor: Dependencias; children: ReactNode }) {
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useDependencias() {
  const deps = useContext(Contexto);
  if (!deps) throw new Error('ProvedorDependencias ausente');
  return deps;
}

export function useQuadro<T>(seletor: (estado: EstadoQuadro) => T): T {
  return useStore(useDependencias().storeQuadro, seletor);
}

export function useEnvio<T>(seletor: (estado: EstadoEnvio) => T): T {
  return useStore(useDependencias().storeEnvio, seletor);
}

export function useColaboracao<T>(seletor: (estado: EstadoColaboracao) => T): T {
  return useStore(useDependencias().storeColaboracao, seletor);
}
